/**
 * PresenceBar — the "who's here now" head-icons cluster, sitting in the sidebar
 * directly above your own name. Each open session heartbeats its own presence to
 * SharePoint and this bar shows the initials of everyone active in the last
 * couple of minutes (e.g. "EP" for Edelyn Pulvinar). You are shown first, with
 * a khaki band round your head.
 *
 * It used to float in the top-right corner over module content, which is
 * exactly where most screens put their own header controls — Dispatch's
 * Assignment Board, QC's Export, Subcontractors' Print all — so the heads
 * covered buttons people were trying to click. It carried ~80 lines of
 * hit-testing that measured what was painted underneath and slid the cluster
 * left to dodge it. In the sidebar it sits in normal flow above the user block,
 * where nothing else competes for the space, so all of that machinery is gone
 * rather than merely disabled: there is nothing left to overlap.
 *
 * Entirely ambient: if presence can't be read or written (list still seeding, a
 * token hiccup, offline), the bar simply renders nothing.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  presenceService,
  PRESENCE_HEARTBEAT_MS,
  type IPresenceUser,
} from "../../lib/sp/PresenceService";
import { displayNameFor } from "../../lib/config/peopleDirectory";

/** Five heads plus a "+N" is about what 240px of sidebar holds comfortably. */
const MAX_AVATARS = 5;

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * The person behind the account, preferred over the role mailbox's own name.
 *
 * Resolved when the head is RENDERED rather than when the heartbeat is written,
 * so adding someone to the directory fixes their head for everyone on the next
 * load — no waiting for that person to sign in again to rewrite their own row.
 */
function personFor(u: IPresenceUser): string {
  return displayNameFor(u.email, u.name);
}

/**
 * Ring colours, hand-picked to sit on the navy without fighting the khaki
 * accent. This replaced `hsl(hash, 70%, 62%)`, which walked the whole colour
 * wheel and put hot pinks and limes next to a khaki-and-navy screen.
 *
 * Eight hues walked evenly round the wheel rather than eight colours that
 * merely looked nice on their own: a first pass paired khaki with a wheat that
 * was indistinguishable from it at 30px, and ran periwinkle, lilac and sky
 * together, which dropped about 35 of the 45 accounts into just two families.
 */
const RING_COLOURS = [
  "#E0906F", // coral    ~20°
  "#C9A46A", // gold     ~38°
  "#9FD08C", // sage    ~100°
  "#5FB3A1", // teal    ~168°
  "#6FB8DE", // sky     ~199°
  "#8FA6D8", // periwinkle ~224°
  "#BE8FD4", // orchid  ~283°
  "#D9788C", // rose    ~345°
];

/** Deterministic pick, so a person keeps one colour across sessions and screens. */
function ringColour(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 997;
  return RING_COLOURS[h % RING_COLOURS.length];
}

/**
 * Two concentric rings around the navy disc: a solid inner one carrying the
 * person's colour, a navy gap, then a faint outer halo of the same colour.
 *
 * The outer bands are box-shadow rather than real borders because a shadow
 * takes no part in layout — the heads keep their 30px footprint, so a tucked
 * cluster still lines up and the sidebar's width is unaffected. The gap band is
 * what makes them read as TWO rings instead of one thick one.
 *
 * "You" adds a third khaki band outside those. It has to be composed here
 * rather than left to a CSS class, because an inline box-shadow would
 * otherwise overwrite the class's.
 */
function ringStyle(seed: string, isMe: boolean): React.CSSProperties {
  const colour = ringColour(seed);
  return {
    borderColor: colour,
    boxShadow: [
      "0 0 0 2px var(--bg-panel)",
      `0 0 0 4px ${colour}66`,
      isMe ? "0 0 0 6px rgba(201, 164, 106, 0.85)" : "",
      "0 2px 8px rgba(0, 0, 0, 0.35)",
    ].filter(Boolean).join(", "),
  };
}

/**
 * `headless` keeps the heartbeat and draws nothing — for a screen with no sidebar
 * (the fulfillment launcher), so being there still counts as online.
 */
export function PresenceBar({ headless = false }: { headless?: boolean } = {}) {
  const { accounts } = useMsal();
  const account = accounts[0];
  const myEmail = (account?.username ?? "").toLowerCase();
  const myName = account?.name ?? account?.username ?? "";

  const [users, setUsers] = useState<IPresenceUser[]>([]);

  useEffect(() => {
    if (!myEmail) return;
    let cancelled = false;

    const beatAndRead = async () => {
      try { await presenceService.heartbeat(myEmail, myName); } catch { /* ambient */ }
      try {
        const active = await presenceService.listActive(myEmail);
        if (!cancelled) setUsers(active);
      } catch { /* ambient */ }
    };

    void beatAndRead();
    const timer = window.setInterval(beatAndRead, PRESENCE_HEARTBEAT_MS);
    // Freshen immediately when the tab is refocused (fast return of coworkers).
    const onFocus = () => void beatAndRead();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; window.clearInterval(timer); window.removeEventListener("focus", onFocus); };
  }, [myEmail, myName]);

  // Current user first, then most-recently-seen others.
  const ordered = useMemo(() => {
    const me = users.filter((u) => u.email.toLowerCase() === myEmail);
    const others = users.filter((u) => u.email.toLowerCase() !== myEmail);
    return [...me, ...others];
  }, [users, myEmail]);

  // "+N" opens the full list of who is online, with each person's account.
  const [open, setOpen] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!barRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  if (headless || ordered.length === 0) return null;

  const shown = ordered.slice(0, MAX_AVATARS);
  const overflow = ordered.length - shown.length;

  return (
    <div className="presence-bar" ref={barRef} aria-label={`${ordered.length} user${ordered.length === 1 ? "" : "s"} online`}>
      {shown.map((u) => (
        <span
          key={u.email}
          className={`presence-avatar ${u.email.toLowerCase() === myEmail ? "presence-avatar--me" : ""}`}
          style={ringStyle(u.email, u.email.toLowerCase() === myEmail)}
          // The account is named too: several role mailboxes share the same two
          // opening letters (customersales1 and cutting1 both read "CU"), so an
          // unmapped head still needs something that tells them apart on hover.
          title={`${personFor(u)}${u.email.toLowerCase() === myEmail ? " (you)" : ""} · ${u.email} · online`}
        >
          {initialsOf(personFor(u))}
        </span>
      ))}
      {overflow > 0 && (
        <button
          type="button"
          className="presence-avatar presence-avatar--more presence-more-btn"
          title={`${overflow} more online — click to see everyone`}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >+{overflow}</button>
      )}
      {open && (
        <div className="presence-pop" role="dialog" aria-label="Who is online">
          <div className="presence-pop-title">Online now · {ordered.length}</div>
          <ul className="presence-pop-list">
            {ordered.map((u) => {
              const me = u.email.toLowerCase() === myEmail;
              return (
                <li key={u.email} className="presence-pop-row" title={u.email}>
                  <span className="presence-pop-head" style={{ borderColor: ringColour(u.email) }}>{initialsOf(personFor(u))}</span>
                  <span className="presence-pop-who">
                    <span className="presence-pop-name">{personFor(u)}{me ? " (you)" : ""}</span>
                    <span className="presence-pop-mail">{u.email}</span>
                  </span>
                  <span className="presence-pop-dot" aria-label="online" />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
